import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Download,
  FileText,
  LayoutDashboard,
  Mail,
  Package,
  Receipt,
  ScrollText,
  Settings,
  Users,
  Wallet,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

function SidebarLink({ to, icon: Icon, label, testId, onClick }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
          "hover:bg-slate-200/70",
          isActive
            ? "bg-slate-900 text-white hover:bg-slate-900 hover:text-white"
            : "text-slate-700"
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

// The `<aside>` here is intentionally viewport-agnostic. Its parent (AppLayout)
// decides whether to render it as a persistent desktop column or as the
// contents of the mobile off-canvas drawer. Do NOT put viewport-hiding
// utilities (e.g. `hidden` at the mobile breakpoint) on this aside — that
// regression hides the sidebar inside the mobile drawer.
export function Sidebar({ onNavigate }) {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAccountant = user?.role === "accountant";
  const canSeeMoney = isAdmin || isAccountant;

  return (
    <aside
      data-testid="sidebar"
      className="flex w-full md:w-64 h-full shrink-0 flex-col border-e border-slate-200 bg-slate-50/80 backdrop-blur"
    >
      <div className="flex h-16 items-center gap-3 border-b border-slate-200 px-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-white text-sm font-semibold tracking-tight">
          IP
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-slate-900" data-testid="sidebar-company">
            {company?.name || t("appName")}
          </div>
          <div className="truncate text-xs text-slate-500">{t("appName")}</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto space-y-1 p-3">
        <SidebarLink to="/app" icon={LayoutDashboard} label={t("sidebar.dashboard")} testId="sidebar-link-dashboard" onClick={onNavigate} />
        <SidebarLink to="/app/invoices" icon={Receipt} label={t("sidebar.invoices")} testId="sidebar-link-invoices" onClick={onNavigate} />
        <SidebarLink to="/app/quotes" icon={ScrollText} label={t("sidebar.quotes")} testId="sidebar-link-quotes" onClick={onNavigate} />
        <SidebarLink to="/app/customers" icon={FileText} label={t("sidebar.customers")} testId="sidebar-link-customers" onClick={onNavigate} />
        <SidebarLink to="/app/products" icon={Package} label={t("sidebar.products")} testId="sidebar-link-products" onClick={onNavigate} />
        {canSeeMoney && (
          <SidebarLink to="/app/payments" icon={Wallet} label={t("sidebar.payments")} testId="sidebar-link-payments" onClick={onNavigate} />
        )}
        {canSeeMoney && (
          <SidebarLink to="/app/exports" icon={Download} label={t("sidebar.exports")} testId="sidebar-link-exports" onClick={onNavigate} />
        )}
        {isAdmin && (
          <SidebarLink
            to="/app/email-outbox"
            icon={Mail}
            label={t("sidebar.emailOutbox")}
            testId="sidebar-link-email-outbox"
            onClick={onNavigate}
          />
        )}
        {isAdmin && (
          <SidebarLink
            to="/app/settings"
            icon={Settings}
            label={t("sidebar.settings")}
            testId="sidebar-link-settings"
            onClick={onNavigate}
          />
        )}
        {isAdmin && (
          <SidebarLink
            to="/app/users"
            icon={Users}
            label={t("sidebar.users")}
            testId="sidebar-link-users"
            onClick={onNavigate}
          />
        )}
      </nav>

      <div className="border-t border-slate-200 p-4 text-xs text-slate-400">
        InvoicePro · v0.2.0
      </div>
    </aside>
  );
}
